import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, CloseButton, createStyles, Group, Stack, Text, TextInput} from '@mantine/core';
import {useMediaQuery} from '@mantine/hooks';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  AnimatedChoiceChip,
  CHIP_HEIGHT_PX,
  CHIP_RADIUS_PX,
  MINIMIZED_ONBOARDING_SURVEY_SHIFT_MS,
  MINIMIZED_ONBOARDING_SURVEY_SHIFT_OFFSET_PX,
  MINIMIZED_ONBOARDING_SURVEY_TOP_INSET_PX,
  ONBOARDING_CHIP_STAGGER_MS,
  ONBOARDING_CHOICES_AREA_MIN_HEIGHT_PX,
  ONBOARDING_PROGRESS_BAR_HEIGHT_PX,
  ONBOARDING_SURVEY_FOOTER_MIN_HEIGHT_PX,
  OnboardingHeadingEnterFade,
  OnboardingProgressBar,
  OnboardingStaggeredEnterFade,
} from './OnboardingComponents';
import {ONBOARDING_SUBTITLE} from './onboardingCopy';
import {OnboardingHeading} from './OnboardingHeading';

/** Side length of the pencil icon badge, in pixels. */
const PENCIL_BADGE_SIZE_PX = 26;

const useFreeTextStyles = createStyles((theme, {selected}: {selected: boolean}) => ({
  closedButton: {
    fontWeight: 400,
    height: CHIP_HEIGHT_PX,
    borderRadius: CHIP_RADIUS_PX,
    border: `1px solid ${selected ? theme.colors.blue[1] : theme.colors.gray[2]}`,
    backgroundColor: selected ? theme.colors.blue[1] : theme.white,
    color: selected ? theme.colors.blue[8] : theme.colors.gray[6],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
      borderColor: theme.colors.blue[1],
      color: theme.colors.blue[8],
    },
  },
  openContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    height: CHIP_HEIGHT_PX,
    border: `1px solid ${selected ? theme.colors.blue[1] : theme.colors.gray[2]}`,
    borderRadius: CHIP_RADIUS_PX,
    padding: `0 ${theme.spacing.md}`,
    cursor: 'text',
    backgroundColor: selected ? theme.colors.blue[1] : theme.white,
    color: selected ? theme.colors.blue[8] : theme.colors.gray[9],
  },
  // A lavender rounded-square badge holding the pencil glyph, matching the design mock.
  pencilBadge: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: PENCIL_BADGE_SIZE_PX,
    height: PENCIL_BADGE_SIZE_PX,
    marginRight: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[6],
    fontSize: theme.fontSizes.sm,
  },
  input: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: theme.fontSizes.sm,
    width: 160,
    padding: 0,
    color: 'inherit',
  },
}));

/**
 * A dashed "Something else" chip that expands into a free-text input. Shared by the onboarding agent
 * survey and the interest/first-chat screen so both present the identical control.
 */
export function FreeTextChoiceButton({
  value,
  onChange,
  onDiscard,
  testComponent,
}: {
  value: string;
  onChange: (value: string) => void;
  onDiscard: () => void;
  /** Component name used for test ids, so each screen keeps stable selectors. */
  testComponent: string;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSelected = value.trim().length > 0;
  const {classes} = useFreeTextStyles({selected: isSelected});

  const handleClick = useCallback(() => {
    setIsOpen(true);
    inputRef.current?.focus();
  }, []);

  const handleBlur = useCallback(() => {
    if (!value.trim()) {
      setIsOpen(false);
      onDiscard();
    }
  }, [value, onDiscard]);

  if (isOpen) {
    return (
      <Box
        className={classes.openContainer}
        onClick={() => inputRef.current?.focus()}
        {...getTestProps({component: testComponent, element: 'something-else-button'})}
      >
        <span className={classes.pencilBadge}>
          <BenchlingIcon icon="pen" aria-hidden={true} />
        </span>
        <TextInput
          size="md"
          ref={inputRef}
          autoFocus={true}
          variant="unstyled"
          classNames={{input: classes.input}}
          styles={{wrapper: {display: 'contents'}}}
          placeholder="Something else"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onBlur={handleBlur}
        />
      </Box>
    );
  }

  return (
    <Button
      aria-pressed={isSelected}
      variant="ghost"
      size="sm"
      className={classes.closedButton}
      leftIcon={
        <span className={classes.pencilBadge}>
          <BenchlingIcon icon="pen" aria-hidden={true} />
        </span>
      }
      onClick={handleClick}
      {...getTestProps({component: testComponent, element: 'something-else-button'})}
    >
      Something else
    </Button>
  );
}

/** Matches the welcome panel horizontal padding (16px). */
const ONBOARDING_SHELL_HORIZONTAL_PADDING_PX = 16;

const useShellStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    maxWidth: 714,
    width: '100%',
    margin: '0 auto',
    padding: `${theme.spacing.xs} ${ONBOARDING_SHELL_HORIZONTAL_PADDING_PX}px ${theme.spacing.md}`,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

/**
 * The outer chrome shared by every onboarding screen: a centered column with a persistent top-right
 * close (✕) button. The body fills the remaining height for the stacked question layout. Keeps the
 * agent survey, the interest/first-chat screen, and the intro screen visually identical.
 */
export function OnboardingScreenShell({
  testComponent,
  onExit,
  exitLabel = 'Exit onboarding',
  children,
}: {
  testComponent: string;
  onExit: () => void;
  exitLabel?: string;
  children: React.ReactNode;
}): JSX.Element {
  const {classes} = useShellStyles();
  return (
    <Box className={classes.container} {...getTestProps({component: testComponent, element: 'root'})}>
      <Group position="right" mb={4}>
        <CloseButton
          aria-label={exitLabel}
          size="sm"
          onClick={onExit}
          {...getTestProps({component: testComponent, element: 'exit-button'})}
        />
      </Group>
      <Box className={classes.body}>{children}</Box>
    </Box>
  );
}

export interface OnboardingChoice {
  /** Choice label, also used as the React key. */
  label: string;
  selected: boolean;
  onSelect: () => void;
}

const useBodyStyles = createStyles((theme, {pinToTop}: {pinToTop: boolean}) => {
  const h2LineHeightPx = parseFloat(theme.headings.sizes.h2.lineHeight as string);
  const spacingXs = parseFloat(theme.spacing.xs);
  const spacingSm = parseFloat(theme.spacing.sm);
  const fontSizeLg = parseFloat(theme.fontSizes.lg);
  const lineHeight = theme.lineHeight as number;
  // Survey heading (three-line title + subtitle), stack gap, and progress bar.
  const headerMinHeight =
    h2LineHeightPx * 3 + spacingXs + fontSizeLg * lineHeight + spacingSm + ONBOARDING_PROGRESS_BAR_HEIGHT_PX;

  return {
    body: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    },
    // Expanded: equal flex spacers center the survey. Minimized: a fixed top inset so chips fit
    // and every question title rests at the same Y; leftover height stays under the footer.
    verticalSpacerTop: pinToTop
      ? {
          flex: 'none',
          height: MINIMIZED_ONBOARDING_SURVEY_TOP_INSET_PX,
          minHeight: MINIMIZED_ONBOARDING_SURVEY_TOP_INSET_PX,
        }
      : {
          flex: '1 1 0',
          minHeight: 0,
        },
    verticalSpacerBottom: {
      flex: '1 1 0',
      minHeight: 0,
    },
    contentStack: {
      flexShrink: 0,
      width: '100%',
      willChange: pinToTop ? 'opacity, transform' : undefined,
    },
    headerSection: {
      flexShrink: 0,
      width: '100%',
      minHeight: headerMinHeight,
    },
    progressSlot: {
      minHeight: ONBOARDING_PROGRESS_BAR_HEIGHT_PX,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
    // Reserved band so chips fading in (and ≤5-row questions) don't shift the header or footer. It
    // floors at the reserved height but grows for a taller question; `overflow: hidden` clips the
    // chips' transient slide-up offset so no scrollbar flashes during the entrance animation.
    choicesSection: {
      flexShrink: 0,
      width: '100%',
      minHeight: ONBOARDING_CHOICES_AREA_MIN_HEIGHT_PX,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: theme.spacing.xl,
      overflow: 'hidden',
    },
    choicesGrid: {
      flexWrap: 'wrap',
    },
    footerSection: {
      flexShrink: 0,
      width: '100%',
      minHeight: ONBOARDING_SURVEY_FOOTER_MIN_HEIGHT_PX,
      marginTop: theme.spacing.xl,
    },
    loadingMessage: {
      fontSize: theme.fontSizes.sm,
      color: theme.colors.gray[6],
      textAlign: 'center',
    },
    layoutPlaceholder: {
      visibility: 'hidden',
      pointerEvents: 'none',
    },
  };
});

/** Three-section survey stack: header, choices band, and footer. */
function OnboardingSurveyStack({
  header,
  choices,
  footer,
  pinToTop,
}: {
  header: React.ReactNode;
  choices: React.ReactNode;
  footer?: React.ReactNode | null;
  /** Minimized sidepanel: pin under a 24px inset instead of vertically centering. */
  pinToTop: boolean;
}): JSX.Element {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [hasShiftedUp, setHasShiftedUp] = useState(!pinToTop || prefersReducedMotion === true);
  const shiftFrameRef = useRef<number | null>(null);
  const shiftSecondFrameRef = useRef<number | null>(null);
  const {classes} = useBodyStyles({pinToTop});

  useLayoutEffect(() => {
    if (!pinToTop || prefersReducedMotion) {
      setHasShiftedUp(true);
      return;
    }
    setHasShiftedUp(false);
    shiftFrameRef.current = requestAnimationFrame(() => {
      shiftFrameRef.current = null;
      shiftSecondFrameRef.current = requestAnimationFrame(() => {
        shiftSecondFrameRef.current = null;
        setHasShiftedUp(true);
      });
    });
    return () => {
      if (shiftFrameRef.current != null) {
        cancelAnimationFrame(shiftFrameRef.current);
        shiftFrameRef.current = null;
      }
      if (shiftSecondFrameRef.current != null) {
        cancelAnimationFrame(shiftSecondFrameRef.current);
        shiftSecondFrameRef.current = null;
      }
    };
  }, [pinToTop, prefersReducedMotion]);

  const shiftStyle: React.CSSProperties = pinToTop
    ? {
        opacity: hasShiftedUp ? 1 : 0,
        transform: hasShiftedUp
          ? 'translateY(0)'
          : `translateY(${MINIMIZED_ONBOARDING_SURVEY_SHIFT_OFFSET_PX}px)`,
        transition:
          prefersReducedMotion || !hasShiftedUp
            ? 'none'
            : `opacity ${MINIMIZED_ONBOARDING_SURVEY_SHIFT_MS}ms ease, transform ${MINIMIZED_ONBOARDING_SURVEY_SHIFT_MS}ms ease`,
      }
    : {};

  return (
    <Box className={classes.body}>
      <Box
        className={classes.verticalSpacerTop}
        aria-hidden={true}
        {...getTestProps({component: 'OnboardingSurveyStack', element: 'top-spacer'})}
      />
      <Stack
        className={classes.contentStack}
        spacing={0}
        style={shiftStyle}
        {...getTestProps({component: 'OnboardingSurveyStack', element: 'content'})}
      >
        <Stack spacing="sm" align="center" className={classes.headerSection}>
          {header}
        </Stack>
        <Box className={classes.choicesSection}>{choices}</Box>
        {footer != null && (
          <Stack align="center" spacing="xs" className={classes.footerSection}>
            {footer}
          </Stack>
        )}
      </Stack>
      <Box className={classes.verticalSpacerBottom} aria-hidden={true} />
    </Box>
  );
}

/**
 * Loading skeleton that reuses the survey stack so the first question does not jump vertically. The
 * spinner sits in the choices band; hidden header/footer placeholders reserve the same space.
 */
export function OnboardingQuestionLoadingView({
  message,
  children,
  isExpanded = true,
}: {
  message?: string;
  children?: React.ReactNode;
  /** When false, pin the survey unit to the minimized top inset instead of centering it. */
  isExpanded?: boolean;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useBodyStyles({pinToTop: !isExpanded});
  return (
    <OnboardingSurveyStack
      pinToTop={!isExpanded}
      header={
        <Stack spacing="sm" align="center" className={classes.layoutPlaceholder} aria-hidden={true}>
          <OnboardingHeading
            title="Tell us about your role"
            subtitle={ONBOARDING_SUBTITLE}
            variant="survey"
          />
          <Box className={classes.progressSlot}>
            <OnboardingProgressBar count={1} />
          </Box>
        </Stack>
      }
      choices={
        <Stack align="center" spacing="sm">
          <CenteredSpinner />
          {message != null && <Text className={classes.loadingMessage}>{message}</Text>}
          {children}
        </Stack>
      }
      footer={
        <Stack align="center" spacing="xs" className={classes.layoutPlaceholder} aria-hidden={true}>
          <Button variant="primary" size="sm" radius="xl" disabled={true} tabIndex={-1}>
            Continue
          </Button>
          <Text size="sm">Skip →</Text>
        </Stack>
      }
    />
  );
}

/**
 * The shared visual layout for an onboarding question: a fixed header (heading, subtitle, progress),
 * a reserved choices band where chips animate in, and a fixed footer (e.g. Continue / Skip). The three
 * sections stack as one unit so the header and footer do not move when chips fade in.
 *
 * Both the agent survey ({@link AIOnboardingScreen}) and the interest/first-chat screen render this
 * so the final static question is visually indistinguishable from the questions preceding it. The
 * choice chips cascade in and replay whenever the question text changes.
 */
export function OnboardingQuestionView({
  question,
  subtitle,
  choices,
  freeText,
  footer,
  progressCount = 0,
  testComponent,
  element = 'choices',
  isExpanded = true,
}: {
  question: string;
  /** Supporting copy under the heading (e.g. "Improve AI responses by sharing details about yourself"). */
  subtitle?: string;
  choices: ReadonlyArray<OnboardingChoice>;
  /** When provided, renders the dashed "Something else" free-text chip after the choices. */
  freeText?: {value: string; onChange: (value: string) => void; onDiscard: () => void};
  /** Footer content pinned to the bottom (e.g. Skip). Omitted when null. */
  footer: React.ReactNode;
  /** Steps reached so far; renders one filled progress segment each above the footer. Omit/0 to hide. */
  progressCount?: number;
  /** Component name used for test ids. */
  testComponent: string;
  /** Test-id element name for the choices container (defaults to 'choices'). */
  element?: string;
  /** When false, pin the survey unit to the minimized top inset instead of centering it. */
  isExpanded?: boolean;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useBodyStyles({pinToTop: !isExpanded});
  return (
    <OnboardingSurveyStack
      pinToTop={!isExpanded}
      header={
        <>
          <OnboardingHeadingEnterFade animationKey={question}>
            <OnboardingHeading title={question} subtitle={subtitle} variant="survey" />
          </OnboardingHeadingEnterFade>
          <Box
            className={classes.progressSlot}
            aria-hidden={progressCount === 0}
            sx={{visibility: progressCount > 0 ? 'visible' : 'hidden'}}
          >
            {progressCount > 0 && (
              <OnboardingProgressBar count={progressCount} testComponent={testComponent} />
            )}
          </Box>
        </>
      }
      choices={
        <Group
          key={question}
          spacing="sm"
          position="center"
          className={classes.choicesGrid}
          {...getTestProps({component: testComponent, element})}
        >
          {choices.map((choice, index) => (
            <AnimatedChoiceChip
              key={choice.label}
              label={choice.label}
              selected={choice.selected}
              onSelect={choice.onSelect}
              index={index}
              animationKey={question}
            />
          ))}
          {freeText != null && (
            // Cascade the free-text chip in one stagger step after the last choice so it enters with
            // the same rise as the others instead of popping in fully-formed.
            <OnboardingStaggeredEnterFade
              animationKey={question}
              delayMs={choices.length * ONBOARDING_CHIP_STAGGER_MS}
              variant="rise"
            >
              <FreeTextChoiceButton
                value={freeText.value}
                onChange={freeText.onChange}
                onDiscard={freeText.onDiscard}
                testComponent={testComponent}
              />
            </OnboardingStaggeredEnterFade>
          )}
        </Group>
      }
      footer={footer}
    />
  );
}
